import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { RecentlyViewed } from '@theme/recently-viewed-products';

/**
 * @typedef {Object} SfRecentlyViewedRefs
 * @property {HTMLElement} carousel
 * @property {HTMLElement} progressTrack
 * @property {HTMLElement} progressIndicator
 * @property {HTMLButtonElement} [prevArrow]
 * @property {HTMLButtonElement} [nextArrow]
 */

const MIN_INDICATOR_RATIO = 0.08;

/** @extends {Component<SfRecentlyViewedRefs>} */
class SfRecentlyViewed extends Component {
  requiredRefs = ['carousel', 'progressTrack', 'progressIndicator'];

  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    this.#hydrate().catch((error) => {
      if (error.name !== 'AbortError') {
        console.error('[sf-recently-viewed] hydration failed', error);
        this.hidden = true;
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#abort = null;
  }

  async #hydrate() {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) {
      this.hidden = true;
      return;
    }

    const viewedIds = RecentlyViewed.getProducts();
    const url = this.#buildFetchUrl(viewedIds);
    if (!url) {
      this.hidden = true;
      return;
    }

    const markup = await sectionRenderer.getSectionHTML(sectionId, false, url);
    const items = this.#extractItems(markup);

    if (items.length === 0) {
      this.hidden = true;
      return;
    }

    const ordered = viewedIds.length > 0 ? this.#reorderByViewedIds(items, viewedIds) : items;
    const { carousel } = this.refs;
    carousel.replaceChildren(...ordered);

    this.#attachCarouselBehaviour();
  }

  /**
   * Reorder items to match the localStorage LIFO order. Unmatched items are appended.
   * @param {HTMLElement[]} items
   * @param {string[]} viewedIds
   * @returns {HTMLElement[]}
   */
  #reorderByViewedIds(items, viewedIds) {
    const byId = new Map(items.map((el) => [el.dataset.productId, el]));
    const ordered = [];
    for (const id of viewedIds) {
      const match = byId.get(String(id));
      if (match) {
        ordered.push(match);
        byId.delete(String(id));
      }
    }
    for (const leftover of byId.values()) ordered.push(leftover);
    return ordered;
  }

  /**
   * @param {string[]} viewedIds
   * @returns {URL | null}
   */
  #buildFetchUrl(viewedIds) {
    const fallbackHandle = this.dataset.fallbackHandle;

    if (viewedIds.length > 0) {
      const url = new URL(Theme.routes.search_url, location.origin);
      url.searchParams.set('q', viewedIds.map((id) => `id:${id}`).join(' OR '));
      url.searchParams.set('resources[type]', 'product');
      return url;
    }

    if (fallbackHandle) {
      return new URL(`/collections/${fallbackHandle}`, location.origin);
    }

    return null;
  }

  /**
   * Parse the fetched section HTML and return the card wrappers.
   * @param {string} markup
   * @returns {HTMLElement[]}
   */
  #extractItems(markup) {
    const parsed = new DOMParser().parseFromString(markup, 'text/html');
    const nodes = parsed.querySelectorAll('.sf-rv__item');
    return Array.from(nodes).filter((el) => el instanceof HTMLElement);
  }

  #attachCarouselBehaviour() {
    const { carousel } = this.refs;
    const signal = this.#abort?.signal;

    this.#updateProgress();
    carousel.addEventListener('scroll', this.#updateProgress, { passive: true, signal });
    window.addEventListener('resize', this.#updateProgress, { signal });
  }

  #updateProgress = () => {
    const { carousel, progressTrack, progressIndicator, prevArrow, nextArrow } = this.refs;

    const maxScroll = carousel.scrollWidth - carousel.clientWidth;
    const progressOuter = progressTrack.parentElement;

    if (maxScroll <= 1) {
      // Everything fits — hide scroll UI.
      // Only center if there are multiple items; single item stays left-aligned.
      const itemCount = carousel.querySelectorAll('.sf-rv__item').length;
      if (itemCount > 1) carousel.classList.add('sf-rv__carousel--fits');
      if (progressOuter) progressOuter.hidden = true;
      this.#toggleArrow(prevArrow, true);
      this.#toggleArrow(nextArrow, true);
      return;
    }

    carousel.classList.remove('sf-rv__carousel--fits');
    if (progressOuter) progressOuter.hidden = false;

    const trackWidth = progressTrack.clientWidth;
    const visibleRatio = Math.max(carousel.clientWidth / carousel.scrollWidth, MIN_INDICATOR_RATIO);
    const indicatorWidthPct = visibleRatio * 100;
    const progress = carousel.scrollLeft / maxScroll;

    progressIndicator.style.width = `${indicatorWidthPct}%`;
    progressIndicator.style.left = `${progress * (100 - indicatorWidthPct)}%`;

    if (trackWidth === 0) return;

    this.#toggleArrow(prevArrow, carousel.scrollLeft <= 0);
    this.#toggleArrow(nextArrow, carousel.scrollLeft >= maxScroll - 1);
  };

  /**
   * @param {HTMLButtonElement | undefined} button
   * @param {boolean} hide
   */
  #toggleArrow(button, hide) {
    if (!button) return;
    button.hidden = hide;
  }

  scrollNext() {
    this.#scrollByCard(1);
  }

  scrollPrev() {
    this.#scrollByCard(-1);
  }

  /** @param {number} direction */
  #scrollByCard(direction) {
    const { carousel } = this.refs;
    const firstItem = carousel.querySelector('.sf-rv__item');
    const step = firstItem instanceof HTMLElement ? firstItem.offsetWidth : carousel.clientWidth * 0.25;
    carousel.scrollBy({ left: step * direction, behavior: 'smooth' });
  }
}

if (!customElements.get('sf-recently-viewed')) {
  customElements.define('sf-recently-viewed', SfRecentlyViewed);
}
